import FastPriorityQueue from "fastpriorityqueue";
import { findKey, floor, includes, omit, toNumber } from "lodash";
import { ERROR_TIMEOUT } from "../../constants/errors";
import * as methods from "../../constants/methods";
import { MessageRequest } from "../../constants/protoTable";
import { ErrorResponse } from "../../proto/Error_pb";
import { Request } from "../../proto/Request_pb";
import ServerError from "../Error/ServerError";
import { isDevMod, msSleep, randomString } from "../utils";
import Client from "./Client";
import RequestWrapper from "./RequestWrapper";
import { INFO_TIME } from "../../constants/methods";
import { InfoTime } from "../../proto/InfoTime_pb";

const apiSingletonEnforcer = Symbol();

let _serverTime = floor(new Date().getTime() / 1000);

export const authNeedless = (actionId: number) =>
  includes(
    [
      methods.INFO_COUNTRY,
      methods.INFO_LOCATION,
      methods.INFO_PAGE,
      methods.INFO_TIME,
      methods.QR_CODE_NEW_DEVICE,
      methods.USER_LOGIN,
      methods.USER_REGISTER,
      methods.USER_TWO_STEP_VERIFICATION_GET_PASSWORD_DETAIL,
      methods.USER_TWO_STEP_VERIFICATION_RECOVER_PASSWORD_BY_ANSWERS,
      methods.USER_TWO_STEP_VERIFICATION_RECOVER_PASSWORD_BY_TOKEN,
      methods.USER_TWO_STEP_VERIFICATION_REQUEST_RECOVERY_TOKEN,
      methods.USER_TWO_STEP_VERIFICATION_VERIFY_PASSWORD,
      methods.USER_VERIFY,
    ],
    actionId
  );

/**
 * @param {number} time
 */
export const setServerTime = (time: number) => {
  _serverTime = time;
};

/**
 * @returns {number}
 */
export const getServerTime = (): number => _serverTime;

export const API_RESPONSE_ACTION_ID_OFFSET = 30000;

const running = new Map();

export type ApiOptions = {
  insecure: boolean;
  durable: boolean;
  priority: number;
  timeout: number;
  important: boolean;
  retry?: number;
};

export const defaultOptions: ApiOptions = {
  insecure: false,
  durable: true,
  priority: 1000,
  timeout: 10000,
  important: false,
};

let pollPendingIsRunning = false;
const pendingComparator = (a: any, b: any) => a.priority > b.priority;
let pending = new FastPriorityQueue(pendingComparator);

export default class Api {
  static _apiSingleton: Api;

  constructor(enforcer: Symbol) {
    if (enforcer !== apiSingletonEnforcer) {
      throw new Error("Cannot construct singleton");
    }
  }

  /**
   * Get Api instance
   * @returns {Api}
   */
  static get instance(): Api {
    if (!this._apiSingleton) {
      this._apiSingleton = new Api(apiSingletonEnforcer);
      this._apiSingleton.checkTimeInfo();
      // eslint-disable-next-line no-unused-expressions,@typescript-eslint/no-unused-expressions
      Client.instance;
    }
    return this._apiSingleton;
  }

  private checkTimeInfo() {
    setTimeout(() => {
      Api.invoke(INFO_TIME, new InfoTime(), { durable: false }).then(
        () => this.checkTimeInfo(),
        () => {
          Client.instance.close({ forceClose: true });
          this.checkTimeInfo();
        }
      );
    }, 5000);
  }

  /**
   * @param {number} actionId
   * @param {object} proto
   * @param {ApiOptions} options
   * @returns {Promise}
   */
  static invoke(actionId: number, proto: MessageRequest, options?: Partial<ApiOptions>) {
    if (30000 < actionId) {
      throw new Error(`Invalid actionId #${actionId}`);
    }

    if (window.Config.debugMode || isDevMod) console.debug(findKey(methods, (id) => id === actionId) + ` 👆 `, proto.toObject());

    /**
     * @type {RequestWrapper}
     */
    let promise: Promise<any>;
    promise = new Promise((resolve, reject) => {
      const wrapper = new RequestWrapper(resolve, reject, Api.instance.__done.bind(Api.instance), actionId, proto, {
        ...defaultOptions,
        ...options,
      });
      wrapper.promise = promise;
      Api.instance.__schedule(wrapper);
    });
    return promise;
  }

  async __schedule(wrapper: RequestWrapper) {
    if (Client.instance.isSecure || (Client.instance.isOnline && wrapper.options.insecure)) {
      this._run(wrapper);
    } else if (wrapper.options.durable) {
      pending.add(wrapper);
    } else {
      const errorResponse = new ErrorResponse();
      errorResponse.setMajorCode(ERROR_TIMEOUT);
      errorResponse.setMinorCode(3);
      wrapper.reject(new ServerError(errorResponse, wrapper.actionId));
    }
  }

  getRequestWrapper(id: string): RequestWrapper {
    return running.get(id);
  }

  _run(wrapper: RequestWrapper) {
    const request = new Request();
    request.setId(randomString(10));
    wrapper.proto.setRequest(request);
    running.set(request.getId(), wrapper);

    Client.instance.packAndSendRequest(wrapper);
  }

  __done(requestId: string) {
    running.delete(requestId);
    this.__pollPending();
  }

  async __pollPending() {
    if (pollPendingIsRunning) {
      return;
    }

    try {
      pollPendingIsRunning = true;

      while (running.size < toNumber(process.env.REACT_APP_API_CONCURRENCY!) && !pending.isEmpty() && Client.instance.isOnline) {
        const wrapper: RequestWrapper = pending.poll();

        if (Client.instance.isSecure || (Client.instance.isOnline && wrapper.options.insecure)) {
          this.__schedule(wrapper);
        } else {
          if (0 < wrapper.options.priority) {
            wrapper.options.priority = floor(wrapper.options.priority / 2, 10);
          } else if (wrapper.options.priority === 0) {
            wrapper.options.priority = -1;
          } else {
            wrapper.options.priority *= 2;
          }
          // pending.add(wrapper);
          await msSleep(100);
        }
      }

      pending.trim();
    } finally {
      pollPendingIsRunning = false;
    }
  }

  addToPending(wrapper: RequestWrapper) {
    pending.add(wrapper);
  }
}
